import { Card, CardContent } from '../../ui';
import { RISK_BANDS } from '../../../constants/risk';

/**
 * Where the roster stands, on the one scale, with the legend in place.
 *
 * Replaces three tiles that counted activity - total scans, "high-risk cases",
 * flagged posts - each a bare integer with no denominator. "21 flagged" is a
 * number; "21 of 92 scanned, 23%" is a finding. The accounts aggregation this
 * reads was already computed on every dashboard load and thrown away.
 */
export function RiskPosture({ accounts, scans }) {
	// The backend bands accounts at 70/30; this reads the raw counts it already
	// sends and re-bands nothing, so the two cannot disagree. Where the backend
	// and the shared scale differ, the numbers shown here are the backend's own
	// buckets, labelled with its thresholds.
	const total = accounts?.totalAccounts || 0;
	const high = accounts?.highRiskAccounts || 0;
	const medium = accounts?.mediumRiskAccounts || 0;
	const low = accounts?.lowRiskAccounts || 0;

	const scanned = scans?.totalPostsScanned || 0;
	const flagged = scans?.totalFlaggedPosts || 0;
	const flaggedRate = scanned > 0 ? (flagged / scanned) * 100 : null;

	const scored = scans?.scoredScans || 0;
	const avg = typeof scans?.avgRisk === 'number' ? Math.round(scans.avgRisk) : null;

	const segments = [
		{ key: 'high', n: high, cls: 'bg-orange-500', label: 'At risk' },
		{ key: 'medium', n: medium, cls: 'bg-amber-500', label: 'Watch' },
		{ key: 'low', n: low, cls: 'bg-green-600', label: 'Clear' },
	].filter((s) => s.n > 0);

	return (
		<Card>
			<CardContent>
				<div className="grid gap-6 md:grid-cols-2">
					<div>
						<h2 className="text-base font-semibold text-foreground">Exposure</h2>
						<p className="mt-1 text-sm text-muted-foreground">
							{total} account{total === 1 ? '' : 's'} watched
						</p>

						{total > 0 && (
							<>
								<div className="mt-3 flex h-2 overflow-hidden rounded-full bg-muted">
									{segments.map((s) => (
										<span key={s.key} className={s.cls} style={{ width: `${(s.n / total) * 100}%` }} />
									))}
								</div>
								<div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
									{segments.map((s) => (
										<span key={s.key} className="inline-flex items-center gap-1.5">
											<span className={`h-2 w-2 rounded-sm ${s.cls}`} />
											{s.n} {s.label}
										</span>
									))}
								</div>
							</>
						)}
					</div>

					<div>
						<h3 className="text-sm font-medium text-foreground">In this window</h3>
						<dl className="mt-2 space-y-2 text-sm">
							<div className="flex items-baseline justify-between gap-3">
								<dt className="text-muted-foreground">Posts flagged</dt>
								<dd className="text-right tabular-nums text-foreground">
									{flaggedRate === null ? (
										<span className="text-muted-foreground">nothing scanned yet</span>
									) : (
										<>
											<span className="font-semibold">{flaggedRate.toFixed(flaggedRate < 10 ? 1 : 0)}%</span>
											<span className="ml-1.5 text-xs text-muted-foreground">{flagged} of {scanned}</span>
										</>
									)}
								</dd>
							</div>
							<div className="flex items-baseline justify-between gap-3">
								<dt className="text-muted-foreground">Average exposure</dt>
								<dd className="text-right tabular-nums text-foreground">
									{avg === null || scored === 0 ? (
										<span className="text-muted-foreground">no scored scans</span>
									) : (
										<>
											<span className="font-semibold">{avg}</span>
											<span className="ml-1.5 text-xs text-muted-foreground">
												across {scored} scored scan{scored === 1 ? '' : 's'}
											</span>
										</>
									)}
								</dd>
							</div>
						</dl>
					</div>
				</div>

				<div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border pt-3 text-xs text-muted-foreground">
					<span className="font-medium uppercase tracking-wide">Exposure scale</span>
					{RISK_BANDS.map((b) => (
						<span key={b.key} className="inline-flex items-center gap-1.5">
							<span className={`h-2 w-2 rounded-sm ${b.dot}`} />
							{b.label} <span className="tabular-nums text-muted-foreground">{b.min}-{b.max}</span>
						</span>
					))}
				</div>
			</CardContent>
		</Card>
	);
}
